import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, StatusBadge, fmtMoney, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Plus, FileText, FileArrowUp, CheckCircle, WarningCircle, DotsThreeVertical, PencilSimple, Trash, Phone, ChatTeardropDots, ArrowSquareOut, PaperPlaneTilt, ReceiptX, MagnifyingGlass, X, CalendarPlus } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ContractDialog } from "@/components/ContractDialog";
import FilesPanel from "@/components/FilesPanel";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { googleVoiceCallUrl, googleVoiceTextUrl } from "@/lib/googleVoice";

const empty = {
  full_name: "", email: "", phone: "",
  language_pairs: [], specialties: [],
  hourly_rate: "", opi_rate: "", opi_unit: "hour",
  vri_rate: "", vri_unit: "hour", translation_rate: "",
  flat_rate: "", location: "",
  address: "", city_state_zip: "", ssn_ein: "", max_travel_miles: "",
  availability: "available", tax_id_last4: "", notes: "",
};

const numeric = (v) => (v === "" || v === null || v === undefined ? 0 : parseFloat(v) || 0);

// Small wrapper that also renders an "All …" option and hides itself when the
// underlying option list is empty.
function FilterSelect({ value, onChange, placeholder, options, testid, width = "" }) {
  if (!options || options.length === 0) return null;
  return (
    <div className={width}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectItem value="all">{placeholder}</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// Extract 2-letter US state code from a "City, ST 12345" style string.
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];
const STATE_RE = new RegExp(`\\b(${US_STATES.join("|")})\\b`, "i");
const parseState = (r) => {
  const src = `${r.city_state_zip || ""} ${r.location || ""} ${r.address || ""}`;
  const m = src.match(STATE_RE);
  return m ? m[1].toUpperCase() : null;
};

// Normalize per-minute rates to per-hour so they can be compared.
const perHour = (rate, unit) => {
  const n = parseFloat(rate) || 0;
  if (n <= 0) return Infinity;
  return unit === "minute" ? n * 60 : n;
};

const SORT_OPTIONS = {
  name: { label: "Name (A-Z)", fn: (a, b) => (a.full_name || "").localeCompare(b.full_name || "") },
  best_inperson: { label: "Best in-person rate", fn: (a, b) => (numeric(a.hourly_rate) || Infinity) - (numeric(b.hourly_rate) || Infinity) },
  best_opi: { label: "Best OPI rate", fn: (a, b) => perHour(a.opi_rate, a.opi_unit) - perHour(b.opi_rate, b.opi_unit) },
  best_vri: { label: "Best VRI rate", fn: (a, b) => perHour(a.vri_rate, a.vri_unit) - perHour(b.vri_rate, b.vri_unit) },
  best_translation: { label: "Best translation rate ($/word)", fn: (a, b) => (numeric(a.translation_rate) || Infinity) - (numeric(b.translation_rate) || Infinity) },
};

export default function Interpreters() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);

  // --- filter + sort state ---
  const [search, setSearch] = useState("");
  const [fState, setFState] = useState("all");
  const [fLanguage, setFLanguage] = useState("all");
  const [fSpecialty, setFSpecialty] = useState("all");
  const [fAvailability, setFAvailability] = useState("all");
  const [includePending, setIncludePending] = useState(false);
  const [sortBy, setSortBy] = useState("name");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["interpreters", includePending],
    queryFn: async () => (await api.get(`/interpreters${includePending ? "?include_pending=true" : ""}`)).data,
  });

  // Build filter options from real data so we only surface values that exist on the roster.
  const filterOptions = useMemo(() => {
    const states = new Set();
    const langs = new Set();
    const specs = new Set();
    for (const r of rows) {
      const s = parseState(r);
      if (s) states.add(s);
      (r.language_pairs || []).forEach((l) => l && langs.add(l.trim()));
      (r.specialties || []).forEach((s) => s && specs.add(s.trim()));
    }
    return {
      states: Array.from(states).sort(),
      languages: Array.from(langs).sort(),
      specialties: Array.from(specs).sort(),
    };
  }, [rows]);

  // Apply filters + sort. `sortBy` also acts as a secondary filter: sorting by
  // "best <mode> rate" drops interpreters that don't offer that service.
  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (fState !== "all" && parseState(r) !== fState) return false;
      if (fLanguage !== "all" && !(r.language_pairs || []).some((l) => (l || "").toLowerCase().includes(fLanguage.toLowerCase()))) return false;
      if (fSpecialty !== "all" && !(r.specialties || []).some((s) => (s || "").toLowerCase() === fSpecialty.toLowerCase())) return false;
      if (fAvailability !== "all" && r.availability !== fAvailability) return false;
      if (!q) return true;
      return `${r.full_name || ""} ${r.email || ""}`.toLowerCase().includes(q);
    });
    // If sorting by a specific rate, hide interpreters who don't have that service priced.
    if (sortBy === "best_inperson")    out = out.filter((r) => numeric(r.hourly_rate) > 0);
    if (sortBy === "best_opi")         out = out.filter((r) => numeric(r.opi_rate) > 0);
    if (sortBy === "best_vri")         out = out.filter((r) => numeric(r.vri_rate) > 0);
    if (sortBy === "best_translation") out = out.filter((r) => numeric(r.translation_rate) > 0);
    out = [...out].sort(SORT_OPTIONS[sortBy]?.fn || SORT_OPTIONS.name.fn);
    return out;
  }, [rows, search, fState, fLanguage, fSpecialty, fAvailability, sortBy]);

  const hasActiveFilters = fState !== "all" || fLanguage !== "all" || fSpecialty !== "all" || fAvailability !== "all" || sortBy !== "name" || search.trim();

  const clearFilters = () => {
    setSearch(""); setFState("all"); setFLanguage("all"); setFSpecialty("all"); setFAvailability("all"); setSortBy("name");
  };

  const save = useMutation({
    mutationFn: async (payload) => {
      if (editing) return (await api.patch(`/interpreters/${editing.interpreter_id}`, payload)).data;
      return (await api.post("/interpreters", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interpreters"] });
      toast.success(editing ? "Interpreter updated" : "Interpreter added");
      setOpen(false); setForm(empty); setEditing(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  // ─── Availability inquiry state ────────────────────────────────────
  const [inquiryFor, setInquiryFor] = useState(null); // interpreter object
  const [inquiryForm, setInquiryForm] = useState({
    scheduled_at: "", duration_hours: 1.5, project_type: "USCIS",
    language: "Haitian Creole", location: "", offered_rate: "", notes: "",
  });
  const sendInquiry = useMutation({
    mutationFn: async (payload) => (await api.post("/interpreter-inquiries", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["comms"] });
      toast.success(`Availability check emailed to ${inquiryFor?.email || "interpreter"}`);
      setInquiryFor(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to send"),
  });
  const openInquiry = (r) => {
    setInquiryForm({
      scheduled_at: "", duration_hours: 1.5,
      project_type: "USCIS",
      language: (r.languages && r.languages[0]) || "Haitian Creole",
      location: "", offered_rate: r.hourly_rate ? String(r.hourly_rate) : "",
      notes: "",
    });
    setInquiryFor(r);
  };

  const del = useMutation({
    mutationFn: async ({ id, force }) => (await api.delete(`/interpreters/${id}${force ? "?force=true" : ""}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interpreters"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Interpreter removed");
    },
    onError: (e, vars) => {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object") {
        const parts = [];
        if (detail.projects) parts.push(`${detail.projects} linked project${detail.projects === 1 ? "" : "s"} (will be unassigned)`);
        if (detail.contracts) parts.push(`${detail.contracts} contract${detail.contracts === 1 ? "" : "s"} (will be deleted)`);
        const msg = `This interpreter has ${parts.join(" and ")}. Continue?`;
        if (window.confirm(msg)) del.mutate({ id: vars.id, force: true });
      } else {
        toast.error(detail || "Delete failed");
      }
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  // When editing, blank-out any zero rates so the input doesn't show a stubborn "0".
  const openEdit = (r) => {
    const blankIfZero = (v) => (v === 0 || v === null || v === undefined ? "" : v);
    setEditing(r);
    setForm({
      ...empty,
      ...r,
      hourly_rate: blankIfZero(r.hourly_rate),
      opi_rate: blankIfZero(r.opi_rate),
      opi_unit: r.opi_unit || "hour",
      vri_rate: blankIfZero(r.vri_rate),
      vri_unit: r.vri_unit || "hour",
      translation_rate: blankIfZero(r.translation_rate),
      flat_rate: blankIfZero(r.flat_rate),
    });
    setOpen(true);
  };

  const setCsv = (key, val) => setForm({ ...form, [key]: val.split(",").map(s => s.trim()).filter(Boolean) });

  return (
    <div data-testid="interpreters-page">
      <PageHeader
        eyebrow="Roster"
        title="Interpreters"
        description="1099 language providers with rates, specialties, and availability."
        actions={
          <Button onClick={openNew} data-testid="add-interpreter-btn" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
            <Plus size={16} weight="bold" className="mr-1.5" /> Add interpreter
          </Button>
        }
      />

      {/* Filter + sort bar */}
      <div className="card-flat p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="interpreter-filters">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name or email…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="interpreter-search"
          />
        </div>
        <FilterSelect testid="filter-language" value={fLanguage} onChange={setFLanguage} placeholder="All languages" options={filterOptions.languages} width="min-w-[170px]" />
        <FilterSelect testid="filter-specialty" value={fSpecialty} onChange={setFSpecialty} placeholder="All specialties" options={filterOptions.specialties} width="min-w-[150px]" />
        <FilterSelect testid="filter-state" value={fState} onChange={setFState} placeholder="All states" options={filterOptions.states} width="min-w-[130px]" />
        <div className="min-w-[150px]">
          <Select value={fAvailability} onValueChange={setFAvailability}>
            <SelectTrigger data-testid="filter-availability"><SelectValue placeholder="Availability" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="busy">Busy</SelectItem>
              <SelectItem value="offline">Offline</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[190px]">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="sort-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(SORT_OPTIONS).map(([k, v]) => (
                <SelectItem key={k} value={k}>Sort: {v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500" data-testid="clear-filters">
            <X size={14} className="mr-1" /> Clear
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-xs text-slate-700 select-none cursor-pointer ml-2" data-testid="include-pending-toggle">
          <input
            type="checkbox"
            checked={includePending}
            onChange={(e) => setIncludePending(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Show pending applicants (no W-9)
        </label>
        <div className="text-xs text-slate-500 ml-auto">
          Showing <b>{displayed.length}</b> of <b>{rows.length}</b>
        </div>
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty title="No interpreters on roster" hint="Add your first 1099 interpreter to start scheduling." />
      ) : displayed.length === 0 ? (
        <Empty title="No interpreters match" hint="Try loosening the filters or clearing them entirely." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="interpreters-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Name</th>
                <th className="text-left px-3 py-3 font-semibold">Languages</th>
                <th className="text-left px-3 py-3 font-semibold">Specialties</th>
                <th className="text-right px-3 py-3 font-semibold">Rates (In-person · OPI · VRI · Trans)</th>
                <th className="text-left px-3 py-3 font-semibold">W-9</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map((r) => (
                <tr key={r.interpreter_id} className="data-row border-t border-slate-100" data-testid={`interpreter-row-${r.interpreter_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/interpreters/${r.interpreter_id}`} className="font-medium text-slate-900 hover:text-blue-800 hover:underline" data-testid={`interpreter-link-${r.interpreter_id}`}>
                        {r.full_name}
                      </Link>
                      {r.approval_status === "pending" && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                          data-testid={`pending-badge-${r.interpreter_id}`}
                          title="Applicant hasn't submitted W-9 yet — availability checks allowed, but not paid assignments."
                        >
                          Pending W-9
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">{r.email || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-slate-700 text-xs">{r.language_pairs?.join(", ") || "—"}</td>
                  <td className="px-3 py-3 text-slate-700 text-xs">{r.specialties?.join(", ") || "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-xs whitespace-nowrap">
                    <div>{fmtMoney(r.hourly_rate)} · {fmtMoney(r.opi_rate || 0)} · {fmtMoney(r.vri_rate || 0)}</div>
                    <div className="text-slate-500">{r.translation_rate ? `$${(r.translation_rate).toFixed(4)}/word` : "no trans."}</div>
                  </td>
                  <td className="px-3 py-3"><W9Cell interpreter={r} qc={qc} /></td>
                  <td className="px-3 py-3"><StatusBadge status={r.availability} /></td>
                  <td className="px-5 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`intp-actions-${r.interpreter_id}`}>
                          <DotsThreeVertical size={16} weight="bold" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-white">
                        <DropdownMenuLabel className="text-xs text-slate-500">Interpreter actions</DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <Link to={`/interpreters/${r.interpreter_id}`}>
                            <ArrowSquareOut size={14} className="mr-2" /> Open profile
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(r)} data-testid={`edit-interpreter-${r.interpreter_id}`}>
                          <PencilSimple size={14} className="mr-2" /> Edit interpreter
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openInquiry(r)}
                          data-testid={`intp-inquiry-${r.interpreter_id}`}
                          disabled={!r.email}
                          className="text-blue-700"
                        >
                          <CalendarPlus size={14} className="mr-2" /> Send availability check…
                        </DropdownMenuItem>
                        <ContractDialog kind="interpreter" interpreterId={r.interpreter_id}>
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid={`intp-contract-${r.interpreter_id}`}>
                            <FileText size={14} className="mr-2" /> Generate 1099 agreement
                          </DropdownMenuItem>
                        </ContractDialog>
                        <DropdownMenuItem
                          data-testid={`intp-send-signing-link-${r.interpreter_id}`}
                          onClick={async () => {
                            try {
                              const resp = await api.post(`/interpreters/${r.interpreter_id}/send-signing-link`);
                              toast.success(`Signing link sent to ${r.email || "interpreter"}`);
                              if (resp.data?.sign_url) navigator.clipboard?.writeText(resp.data.sign_url)?.catch(() => {});
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "Failed to send signing link");
                            }
                          }}
                        >
                          <PaperPlaneTilt size={14} className="mr-2" /> Send signing link (e-sign)
                        </DropdownMenuItem>
                        {!r.w9_path && (
                          <>
                            <DropdownMenuItem
                              data-testid={`intp-request-w9-${r.interpreter_id}`}
                              disabled={!r.email}
                              onClick={async () => {
                                try {
                                  await api.post(`/interpreters/${r.interpreter_id}/request-tax-form`, { tax_form_type: "w9" });
                                  toast.success(`W-9 request emailed to ${r.email}`);
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Failed to send request");
                                }
                              }}
                            >
                              <ReceiptX size={14} className="mr-2" /> Request W-9 by email
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              data-testid={`intp-request-w8ben-${r.interpreter_id}`}
                              disabled={!r.email}
                              onClick={async () => {
                                try {
                                  await api.post(`/interpreters/${r.interpreter_id}/request-tax-form`, { tax_form_type: "w8ben" });
                                  toast.success(`W-8BEN request emailed to ${r.email}`);
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Failed to send request");
                                }
                              }}
                            >
                              <ReceiptX size={14} className="mr-2" /> Request W-8BEN by email (foreign)
                            </DropdownMenuItem>
                          </>
                        )}
                        {r.phone && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-slate-500">Google Voice</DropdownMenuLabel>
                            <DropdownMenuItem asChild>
                              <a href={googleVoiceCallUrl(r.phone)} target="_blank" rel="noreferrer">
                                <Phone size={14} className="mr-2" /> Call {r.phone}
                              </a>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={googleVoiceTextUrl(r.phone)} target="_blank" rel="noreferrer">
                                <ChatTeardropDots size={14} className="mr-2" /> Text {r.phone}
                              </a>
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            setTimeout(() => {
                              if (window.confirm(`Delete interpreter "${r.full_name}"? This cannot be undone.`)) del.mutate({ id: r.interpreter_id, force: false });
                            }, 50);
                          }}
                          className="text-red-600"
                          data-testid={`delete-interpreter-${r.interpreter_id}`}
                        >
                          <Trash size={14} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg bg-white overflow-y-auto">
          <SheetHeader><SheetTitle>{editing ? "Edit interpreter" : "New interpreter"}</SheetTitle></SheetHeader>
          <div className="mt-6 space-y-4" data-testid="interpreter-form">
            <Field label="Full name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="intp-name" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
              <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            </div>
            <Field label="Language pairs (comma separated)">
              <Input placeholder="French↔English, Haitian Creole↔English" value={form.language_pairs.join(", ")} onChange={(e) => setCsv("language_pairs", e.target.value)} data-testid="intp-languages" />
            </Field>
            <Field label="Specialties (comma separated)">
              <Input placeholder="USCIS, Medical, Legal" value={form.specialties.join(", ")} onChange={(e) => setCsv("specialties", e.target.value)} />
            </Field>
            <div className="pt-2 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">Rates</div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="In-person ($/hour)">
                  <Input type="number" step="0.01" min="0" inputMode="decimal" placeholder="e.g. 65.00"
                    value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    data-testid="intp-hourly" />
                </Field>
                <Field label={`OPI phone ($/${form.opi_unit === "min" ? "minute" : "hour"})`}>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="0" inputMode="decimal"
                      placeholder={form.opi_unit === "min" ? "e.g. 1.50" : "e.g. 55.00"}
                      value={form.opi_rate}
                      onChange={(e) => setForm({ ...form, opi_rate: e.target.value })}
                      onFocus={(e) => e.target.select()}
                      data-testid="intp-opi" className="flex-1" />
                    <select value={form.opi_unit}
                      onChange={(e) => setForm({ ...form, opi_unit: e.target.value })}
                      data-testid="intp-opi-unit"
                      className="border border-slate-300 rounded-md px-2 text-sm bg-white">
                      <option value="hour">/ hour</option>
                      <option value="min">/ minute</option>
                    </select>
                  </div>
                </Field>
                <Field label={`VRI video ($/${form.vri_unit === "min" ? "minute" : "hour"})`}>
                  <div className="flex gap-2">
                    <Input type="number" step="0.01" min="0" inputMode="decimal"
                      placeholder={form.vri_unit === "min" ? "e.g. 2.00" : "e.g. 70.00"}
                      value={form.vri_rate}
                      onChange={(e) => setForm({ ...form, vri_rate: e.target.value })}
                      onFocus={(e) => e.target.select()}
                      data-testid="intp-vri" className="flex-1" />
                    <select value={form.vri_unit}
                      onChange={(e) => setForm({ ...form, vri_unit: e.target.value })}
                      data-testid="intp-vri-unit"
                      className="border border-slate-300 rounded-md px-2 text-sm bg-white">
                      <option value="hour">/ hour</option>
                      <option value="min">/ minute</option>
                    </select>
                  </div>
                </Field>
                <Field label="Translation ($/word)">
                  <Input type="number" step="0.0001" min="0" inputMode="decimal" placeholder="e.g. 0.15"
                    value={form.translation_rate}
                    onChange={(e) => setForm({ ...form, translation_rate: e.target.value })}
                    onFocus={(e) => e.target.select()}
                    data-testid="intp-translation" />
                </Field>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
              <Field label="Availability">
                <Select value={form.availability} onValueChange={(v) => setForm({ ...form, availability: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["available","busy","off"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Tax ID last 4"><Input value={form.tax_id_last4} onChange={(e) => setForm({ ...form, tax_id_last4: e.target.value })} /></Field>
            <div className="pt-2 border-t border-slate-200">
              <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">W-9 · Contract fields</div>
              <div className="space-y-3">
                <Field label="Street address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="intp-address" /></Field>
                <Field label="City, State, ZIP"><Input placeholder="Bethesda, MD 20814" value={form.city_state_zip} onChange={(e) => setForm({ ...form, city_state_zip: e.target.value })} data-testid="intp-csz" /></Field>
                <Field label="Max travel distance (miles)">
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 50"
                    value={form.max_travel_miles ?? ""}
                    onChange={(e) => setForm({ ...form, max_travel_miles: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
                    data-testid="intp-max-travel-miles"
                  />
                </Field>
                <Field label="SSN or EIN (full — for W-9)">
                  <Input value={form.ssn_ein} onChange={(e) => setForm({ ...form, ssn_ein: e.target.value })} placeholder="XX-XXXXXXX" data-testid="intp-ssn" />
                </Field>
              </div>
            </div>
            <Field label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>

            {editing && (
              <div className="pt-4 border-t border-slate-200">
                <FilesPanel entityType="interpreter" entityId={editing.interpreter_id} />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={() => save.mutate({
                  ...form,
                  hourly_rate: numeric(form.hourly_rate),
                  opi_rate: numeric(form.opi_rate),
                  vri_rate: numeric(form.vri_rate),
                  translation_rate: numeric(form.translation_rate),
                  flat_rate: numeric(form.flat_rate),
                  max_travel_miles: form.max_travel_miles === "" ? null : form.max_travel_miles,
                })}
                className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="intp-save">
                {editing ? "Save changes" : "Add interpreter"}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Availability inquiry dialog */}
      <Dialog open={!!inquiryFor} onOpenChange={(v) => !v && setInquiryFor(null)}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="inquiry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus size={18} /> Send availability check
            </DialogTitle>
          </DialogHeader>
          {inquiryFor && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Sending to</div>
                <div className="font-semibold text-slate-900 mt-1">{inquiryFor.name}</div>
                <div className="text-xs text-slate-600">{inquiryFor.email} · Standard rate ${(inquiryFor.hourly_rate || 0).toFixed(2)}/hr</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date & time">
                  <Input
                    type="datetime-local"
                    value={inquiryForm.scheduled_at}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, scheduled_at: e.target.value })}
                    data-testid="inquiry-scheduled-at"
                  />
                </Field>
                <Field label="Duration (hours)">
                  <Input
                    type="number" step="0.25" min="0.25"
                    value={inquiryForm.duration_hours}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, duration_hours: parseFloat(e.target.value) })}
                    data-testid="inquiry-duration"
                  />
                </Field>
                <Field label="Type">
                  <Select value={inquiryForm.project_type} onValueChange={(v) => setInquiryForm({ ...inquiryForm, project_type: v })}>
                    <SelectTrigger data-testid="inquiry-type"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="USCIS">USCIS</SelectItem>
                      <SelectItem value="Medical">Medical</SelectItem>
                      <SelectItem value="Legal">Legal</SelectItem>
                      <SelectItem value="Translation">Translation</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Language">
                  <Input
                    value={inquiryForm.language}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, language: e.target.value })}
                    data-testid="inquiry-language"
                  />
                </Field>
                <Field label="Location (optional)">
                  <Input
                    value={inquiryForm.location}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, location: e.target.value })}
                    placeholder="e.g. Boston USCIS Field Office"
                    data-testid="inquiry-location"
                  />
                </Field>
                <Field label="Offered rate ($/hr)">
                  <Input
                    type="number" step="0.01"
                    value={inquiryForm.offered_rate}
                    onChange={(e) => setInquiryForm({ ...inquiryForm, offered_rate: e.target.value })}
                    placeholder="Leaves blank = use their standard"
                    data-testid="inquiry-offered-rate"
                  />
                </Field>
              </div>
              <Field label="Notes for the interpreter (optional)">
                <Textarea
                  rows={3}
                  value={inquiryForm.notes}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, notes: e.target.value })}
                  placeholder="e.g. 'Client speaks only Kreyòl, complex asylum case, prior counsel present.'"
                  data-testid="inquiry-notes"
                />
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setInquiryFor(null)}>Cancel</Button>
            <Button
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              disabled={!inquiryForm.scheduled_at || sendInquiry.isPending}
              onClick={() => sendInquiry.mutate({
                interpreter_id: inquiryFor.interpreter_id,
                scheduled_at: new Date(inquiryForm.scheduled_at).toISOString(),
                duration_hours: parseFloat(inquiryForm.duration_hours) || 1.5,
                project_type: inquiryForm.project_type,
                language: inquiryForm.language,
                location: inquiryForm.location || null,
                offered_rate: inquiryForm.offered_rate ? parseFloat(inquiryForm.offered_rate) : null,
                notes: inquiryForm.notes || null,
              })}
              data-testid="inquiry-send-btn"
            >
              <PaperPlaneTilt size={14} className="mr-1.5" />
              {sendInquiry.isPending ? "Sending…" : "Send availability check"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function W9Cell({ interpreter, qc }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const hasW9 = !!interpreter.w9_path;

  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await fetch(`${API}/interpreters/${interpreter.interpreter_id}/w9`, {
        method: "POST", body: fd, credentials: "include",
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      toast.success("W-9 uploaded");
      qc.invalidateQueries({ queryKey: ["interpreters"] });
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const view = async () => {
    const r = await fetch(`${API}/interpreters/${interpreter.interpreter_id}/w9`, { credentials: "include" });
    if (!r.ok) return toast.error("Failed to load W-9");
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <div className="flex items-center gap-1">
      {hasW9 ? (
        <>
          <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200" title={interpreter.w9_filename}>
            <CheckCircle size={12} weight="fill" /> On file
          </span>
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={view} data-testid={`view-w9-${interpreter.interpreter_id}`}>View</Button>
        </>
      ) : (
        <span className="badge-pill bg-amber-50 text-amber-700 border-amber-200">
          <WarningCircle size={12} weight="fill" /> Missing
        </span>
      )}
      <input ref={fileRef} type="file" accept=".pdf,image/png,image/jpeg,image/webp" onChange={doUpload} className="hidden" data-testid={`w9-input-${interpreter.interpreter_id}`} />
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`upload-w9-${interpreter.interpreter_id}`}>
        <FileArrowUp size={12} className="mr-1" /> {uploading ? "…" : hasW9 ? "Replace" : "Upload"}
      </Button>
    </div>
  );
}
